import { enums, formatters, selectors } from "@fraction/shared";
import { formatRelative } from "date-fns";
import _ from "lodash";
import { ExternalLink, Loader, Maximize2, Minimize2, X } from "lucide-react";
import {
  type ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import { toast } from "react-toastify";
import { ReadyState } from "react-use-websocket";
import { useAuthUser } from "src/auth/useAuth";
import Avatar from "src/components/Avatar";
import { CompactWindowFrame } from "src/components/CompactWindowFrame";
import ModalBox from "src/components/ModalBox";
import Skeleton from "src/components/Skeleton";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { useChat } from "src/hooks/useChat";
import { isTauriRuntime } from "src/lib/runtime/environment";
import { useChatModalStore } from "src/stores/chatModalStore";
import {
  listenForApplicationChatOwnership,
  openDetachedApplicationChatWindow,
  prewarmApplicationChatWindow,
} from "src/utilities/openApplicationChat";
import { cn } from "src/utilities/shadcnUtils";

const chatColor: Record<string, string> = {
  [enums.ChatType.BROKER_EMPLOYEE]: "bg-green",
  [enums.ChatType.LENDER_EMPLOYEE]: "bg-blue",
  [enums.ChatType.CONVEYANCER_EMPLOYEE]: "bg-purple",
  [enums.ChatType.LENDER_CONVEYANCER]: "bg-purple",
  [enums.ChatType.LENDER_BROKER_EMPLOYEE]: "bg-orange",
};

const chatLabel: Record<string, string> = {
  [enums.ChatType.BROKER_EMPLOYEE]: "Broker & Employee",
  [enums.ChatType.LENDER_EMPLOYEE]: "Lender & Employee",
  [enums.ChatType.CONVEYANCER_EMPLOYEE]: "Conveyancer & Employee",
  [enums.ChatType.LENDER_CONVEYANCER]: "Lender & Conveyancer",
  [enums.ChatType.LENDER_BROKER_EMPLOYEE]: "Lender, Broker & Employee",
};

export function ChatModalContent({
  applicationId,
  chatType,
  onClose,
  applicantNames,
  applicationEid,
  native,
  compact,
  onToggleCompact,
  onUserInput,
}: {
  applicationId: string;
  chatType: enums.ChatType;
  onClose: () => void;
  applicantNames?: string;
  applicationEid?: string;
  native?: boolean;
  /** Rendered inside a CompactWindowFrame: fill the frame and make the header the drag handle. */
  compact?: boolean;
  /** When set, renders a Shrink/Expand button that toggles between the modal and the floating window. */
  onToggleCompact?: () => void;
  onUserInput?: () => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const initialScrollDone = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { preference } = useAccountViewPreference();
  const { user } = useAuthUser();

  const {
    chats,
    members,
    sendMessage,
    isPending,
    isLoading,
    readyState,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChat({
    applicationId,
    type: chatType,
    enabled: true,
  });

  const [text, setText] = useState("");

  // Scroll to bottom on initial load
  useEffect(() => {
    if (chats?.length && !initialScrollDone.current) {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
        initialScrollDone.current = true;
      }
    }
  }, [chats]);

  // Save scroll height before older messages are prepended
  useLayoutEffect(() => {
    if (isFetchingNextPage) {
      const container = scrollContainerRef.current;
      if (container) {
        prevScrollHeightRef.current = container.scrollHeight;
      }
    }
  }, [isFetchingNextPage]);

  // Restore scroll position after older messages are prepended
  useEffect(() => {
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0) {
      const container = scrollContainerRef.current;
      if (container) {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
    }
  }, [isFetchingNextPage, chats]);

  // Auto-scroll to bottom when new messages arrive (if already near bottom)
  const prevChatLengthRef = useRef(0);
  useEffect(() => {
    const flatLength = chats?.reduce((acc, g) => acc + (g.chats?.length || 0), 0) || 0;
    if (flatLength > prevChatLengthRef.current && initialScrollDone.current) {
      const container = scrollContainerRef.current;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
    prevChatLengthRef.current = flatLength;
  }, [chats]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
      onUserInput?.();
    },
    [onUserInput]
  );

  const handlePressSend = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault?.();
      const textToSend = text.trim();
      if (!textToSend || !applicationId) return;
      setText("");
      sendMessage({ message: textToSend, applicationId });
    },
    [text, applicationId, sendMessage]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handlePressSend();
      }
    },
    [handlePressSend]
  );

  const memberNames = useMemo(
    () => _.uniq(members?.map((member) => formatters.user.firstName(member)?.trim()).filter(Boolean) || []),
    [members]
  );

  const chatWithAnonymizedUser = useMemo(
    () =>
      chats?.map((chat) => ({
        ...chat,
        user: selectors.user.anonymizedUser(chat.user, preference === "broker"),
        chats: chat.chats?.map((message) => ({
          ...message,
          user: selectors.user.anonymizedUser(chat.user, preference === "broker"),
        })),
      })),
    [chats, preference]
  );

  const headerColor = ["employee", "lender"].includes(preference)
    ? chatColor[chatType] || "bg-green"
    : "bg-green";

  return (
    <div
      className={cn(
        "flex flex-col",
        native ? "h-screen" : compact ? "h-full min-h-0" : "h-[70vh] max-h-[700px]"
      )}
    >
      {/* Header */}
      <div
        data-tauri-drag-region={native ? "" : undefined}
        data-drag-handle={compact ? "" : undefined}
        className={cn(
          "flex items-center gap-3",
          compact ? "cursor-move select-none px-3 py-2" : "px-5 py-3",
          !native && !compact && "rounded-t-lg",
          native
            ? "min-h-14 border-b border-border bg-card pl-[84px] text-card-foreground"
            : cn("text-primary-foreground", headerColor)
        )}
      >
        <div
          data-tauri-drag-region={native ? "" : undefined}
          className={cn("min-w-0 flex-1", native && "flex h-full items-center gap-2")}
        >
          <div data-tauri-drag-region={native ? "" : undefined} className="flex items-center gap-2">
            {readyState === ReadyState.OPEN ? (
              <div className="h-2 w-2 min-h-2 min-w-2 rounded-full border border-background bg-green" />
            ) : (
              <div className="h-2 w-2 min-h-2 min-w-2 rounded-full border border-background bg-red" />
            )}
            <p data-tauri-drag-region={native ? "" : undefined} className="text-sm font-medium">
              {chatLabel[chatType] || "Chat"}
              {applicantNames ? ` · ${applicantNames}` : ""}
            </p>
          </div>
          {memberNames.length > 0 && native ? (
            <p data-tauri-drag-region="" className="min-w-0 truncate text-xs text-muted-foreground">
              {memberNames.join(", ")}
            </p>
          ) : null}
          {memberNames.length > 0 && !native ? (
            <p className="mt-0.5 truncate text-xs opacity-80">{memberNames.join(", ")}</p>
          ) : null}
        </div>
        {!native ? (
          <div className="flex shrink-0 items-center gap-1">
            <Link
              to={`/app/${applicationId}`}
              // The floating window stays open while you navigate; the modal gets out of the way.
              onClick={compact ? undefined : onClose}
              data-no-drag
              className="flex items-center gap-1 rounded-md bg-card/20 px-2 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-card/30"
            >
              <ExternalLink className="h-3 w-3" />
              {applicationEid || "Open"}
            </Link>
            {onToggleCompact ? (
              <button
                type="button"
                onClick={onToggleCompact}
                aria-label={compact ? "Expand to full size" : "Shrink to floating window"}
                title={compact ? "Expand to full size" : "Shrink to floating window"}
                className="rounded-md p-1 transition-colors hover:bg-card/20"
              >
                {compact ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat"
              className="rounded-md p-1 transition-colors hover:bg-card/20"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn("flex-1 overflow-y-auto overscroll-contain", native || compact ? "p-4" : "p-5")}
      >
        {isFetchingNextPage ? (
          <div className="flex justify-center py-2">
            <Loader height={16} className="animate-spin text-muted-foreground" />
          </div>
        ) : null}
        {!chatWithAnonymizedUser?.length && isLoading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map((x) => (
              <Skeleton key={x} className="h-14 w-full" />
            ))}
          </div>
        ) : null}
        {chatWithAnonymizedUser?.map((chat, chatGroupIdx) => {
          const isLastChatGroup = chatGroupIdx === (chats?.length || 1) - 1;
          const isThisUser = chat.user?.id === user?.id;
          const key = `${chat.user?.id}-${chat.chats?.[0]?.id}`;
          if (!key) return null;
          return (
            <div className="relative flex flex-row gap-2 pl-8 pr-4" key={key}>
              {!isThisUser ? (
                <Avatar
                  className="align-end absolute left-[-4px] top-9 h-[26px] w-[26px] text-xs"
                  title={formatters.user.firstName(chat.user)}
                >
                  {chat.user?.firstName?.[0]}
                  {chat.user?.lastName?.[0]}
                </Avatar>
              ) : null}
              <div className={cn("flex w-full flex-col gap-3", isThisUser ? "items-end" : "items-start")}>
                {chat?.chats?.map((message, index) => {
                  const isLastChat = index === (chat?.chats?.length || 1) - 1 && isLastChatGroup;
                  return (
                    <div
                      className={cn("flex w-full flex-col", isThisUser ? "items-end" : "items-start")}
                      key={message.id}
                    >
                      <div className="flex w-full items-center justify-center">
                        {message.createdAt ? (
                          <p className="mt-2 text-center align-middle text-xs text-gray">
                            {formatters.string.capitalize(formatRelative(message.createdAt, new Date()))}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex w-fit max-w-[70%] flex-col">
                        <p
                          className={cn(
                            "text-[8px] text-gray",
                            isThisUser ? "translate-x-[-16px] text-right" : "translate-x-[16px] text-left"
                          )}
                        >
                          {message.user?.firstName}
                        </p>
                        <div className="flex flex-row items-center gap-1">
                          <div
                            className={cn(
                              "flex flex-col rounded-[8px] p-2 px-3 text-start text-xs",
                              isThisUser ? "bg-green-400" : "bg-muted-foreground/40"
                            )}
                          >
                            {message.message
                              ?.split("\n")
                              .filter(Boolean)
                              .map((x) => (
                                <p key={x}>{x}</p>
                              ))}
                          </div>
                          {isLastChat && isPending && (
                            <Loader height={16} className="animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <form
        onSubmit={handlePressSend}
        className={cn(
          "flex flex-row items-center gap-2 border-t",
          native || compact ? "bg-card p-3" : "px-5 py-3"
        )}
      >
        <TextareaAutosize
          maxRows={5}
          ref={textareaRef}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          value={text}
          placeholder="Type a message..."
          className="w-full min-w-0 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
        />
        <button
          type="submit"
          className={cn(
            "flex h-9 items-center justify-center bg-green text-sm font-medium text-primary-foreground",
            native ? "rounded-lg px-4" : "w-20 rounded-full"
          )}
        >
          Send
        </button>
      </form>
    </div>
  );
}

export const ChatModal = memo(function ChatModal() {
  const isOpen = useChatModalStore((s) => s.isOpen);
  const applicationId = useChatModalStore((s) => s.applicationId);
  const chatType = useChatModalStore((s) => s.chatType);
  const applicantNames = useChatModalStore((s) => s.applicantNames);
  const applicationEid = useChatModalStore((s) => s.applicationEid);
  const closeChat = useChatModalStore((s) => s.closeChat);
  const compact = useChatModalStore((s) => s.compact);
  const toggleCompact = useChatModalStore((s) => s.toggleCompact);

  useEffect(() => {
    if (!isTauriRuntime()) return;
    void prewarmApplicationChatWindow().catch((error) =>
      console.warn("Could not prewarm the application chat window.", error)
    );
    let disposed = false;
    let unlisten: (() => void) | undefined;
    void listenForApplicationChatOwnership().then((stopListening) => {
      if (disposed) stopListening();
      else unlisten = stopListening;
    });
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, []);

  useEffect(() => {
    if (!isTauriRuntime() || !isOpen || !applicationId || !chatType) return;
    const payload = { applicationId, chatType, applicantNames, applicationEid };
    closeChat();
    void openDetachedApplicationChatWindow(payload).catch((error) =>
      toast.error(`Could not open application chat window: ${String(error)}`)
    );
  }, [applicationEid, applicantNames, applicationId, chatType, closeChat, isOpen]);

  if (isTauriRuntime()) return null;

  const content =
    isOpen && applicationId && chatType ? (
      <ChatModalContent
        // Remount on mode switch so the message list re-scrolls to the bottom in its new container.
        key={compact ? "compact" : "modal"}
        applicationId={applicationId}
        chatType={chatType}
        onClose={closeChat}
        applicantNames={applicantNames}
        applicationEid={applicationEid}
        compact={compact}
        onToggleCompact={toggleCompact}
      />
    ) : null;

  if (compact) {
    if (!content) return null;
    return (
      <CompactWindowFrame storageId="compact-chat" width={420} height={560}>
        {content}
      </CompactWindowFrame>
    );
  }

  return (
    <ModalBox
      open={isOpen}
      onClose={closeChat}
      width="600px"
      closeButton={false}
      closeOnClickBackdrop
      closeOnEscape
    >
      {content ?? <div />}
    </ModalBox>
  );
});
